import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import VerticalDotsIcon from '@mui/icons-material/MoreVert';
import LabelIcon from '@mui/icons-material/LabelOutlined';
import FilterIcon from '@mui/icons-material/FilterListRounded';
import { Plus, Upload } from 'lucide-react';

import { Flex } from 'src/components/flex';
import { ImportLeads } from 'src/components/bulk-import';
import PageHeader from 'src/components/page-header';
import Responsive from 'src/components/responsive';
import { useLeadsPageContext } from 'src/pages/landlord/leads/provider';
import Search from 'src/pages/landlord/leads/components/header/LeadsSearch';
import LeadsFilterButton from 'src/pages/landlord/leads/components/header/LeadsFilterButton';
import SelectGrouping from 'src/pages/landlord/leads/components/header/SelectGrouping';
import FilterStatus from 'src/pages/landlord/leads/components/header/LeadsFilterStatus';
import SortLeads from 'src/pages/landlord/leads/components/header/SortLeads';
import styles from 'src/pages/landlord/leads/components/header/LeadsHeader.module.css';

export default function LeadsHeader() {
    const {
        setIsCreateLeadModalOpen,
        setIsEditLeadLabelsModalOpen,
        setFilterOpen,
    } = useLeadsPageContext();

    const [isImportLeadsModalOpen, setIsImportLeadsModalOpen] = useState(false);
    const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState<HTMLElement>();

    const [anchorEl, setAnchorEl] = useState<HTMLElement>();
    const closeMenu = () => setAnchorEl(undefined);
    const closeCreateMenu = () => setCreateMenuAnchorEl(undefined);

    return (
        <>
            <PageHeader
                search={(
                    <Flex alignItems="center" width="100%">
                        <Search />
                        <Responsive desktop>
                            <Flex width={150} sx={{ flexShrink: 0 }}>
                                <SelectGrouping />
                            </Flex>
                        </Responsive>
                    </Flex>
                )}
                controls={(
                    <Responsive mobile>
                        <Flex width="100%">
                            <SelectGrouping />
                        </Flex>
                    </Responsive>
                )}
                filter={<LeadsFilterButton />}
                sort={<SortLeads />}
                actions={(
                    <>
                        <Flex
                            justifyContent="flex-end"
                            sx={{ '@media (max-width: 1200px)': { display: 'none' } }}
                        >
                            <Button
                                variant="contained"
                                onClick={(e) => setCreateMenuAnchorEl(e.currentTarget)}
                                startIcon={<Plus size={18} />}
                                data-testid="new-lead-button"
                            >
                                New Lead
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => setIsEditLeadLabelsModalOpen(true)}
                                startIcon={<LabelIcon />}
                                data-testid="lead-labels-button"
                            >
                                Labels
                            </Button>
                        </Flex>

                        <IconButton
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            edge="end"
                            sx={{ '@media (min-width: 1201px)': { display: 'none' } }}
                        >
                            <VerticalDotsIcon />
                        </IconButton>
                    </>
                )}
                status={<FilterStatus />}
            />

            <Menu
                anchorEl={createMenuAnchorEl}
                open={!!createMenuAnchorEl}
                onClose={closeCreateMenu}
            >
                <MenuItem
                    className={styles.createMenuItem}
                    data-testid="create-lead-manually-menu-item"
                    onClick={() => {
                        setIsCreateLeadModalOpen(true);
                        closeCreateMenu();
                    }}
                >
                    <Plus size={18} />
                    <ListItemText>
                        <Typography variant="body2">Create One</Typography>
                    </ListItemText>
                </MenuItem>

                <MenuItem
                    className={styles.createMenuItem}
                    data-testid="import-leads-csv-menu-item"
                    onClick={() => {
                        setIsImportLeadsModalOpen(true);
                        closeCreateMenu();
                    }}
                >
                    <Upload size={18} />
                    <ListItemText>
                        <Typography variant="body2">Import Leads CSV</Typography>
                    </ListItemText>
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={closeMenu}
            >
                <MenuItem onClick={() => {
                    setIsCreateLeadModalOpen(true);
                    closeMenu();
                }}
                >
                    <ListItemIcon>
                        <Plus size={18} />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography>New Lead</Typography>
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    setIsEditLeadLabelsModalOpen(true);
                    closeMenu();
                }}
                >
                    <ListItemIcon>
                        <LabelIcon />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography>Labels</Typography>
                    </ListItemText>
                </MenuItem>

                <MenuItem onClick={() => {
                    setFilterOpen(true);
                    closeMenu();
                }}
                >
                    <ListItemIcon>
                        <FilterIcon />
                    </ListItemIcon>
                    <ListItemText>
                        <Typography>Filter</Typography>
                    </ListItemText>
                </MenuItem>
            </Menu>

            {isImportLeadsModalOpen && (
                <ImportLeads
                    isOpened={isImportLeadsModalOpen}
                    onClose={() => setIsImportLeadsModalOpen(false)}
                />
            )}
        </>
    );
}
